import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { SharedLayoutUltimate } from '../components/layout/SharedLayoutUltimate';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: ${theme.spacing['3xl']} ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing['2xl']} ${theme.spacing.md};
  }
`;

const Title = styled.h1`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.primary.dark};
  margin-bottom: ${theme.spacing['2xl']};
  text-align: center;
`;

const Section = styled.section`
  margin-bottom: ${theme.spacing['2xl']};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${theme.colors.primary.dark};
  margin-bottom: ${theme.spacing.lg};
  border-bottom: 2px solid #FFD700;
  padding-bottom: ${theme.spacing.sm};
`;

const Paragraph = styled.p`
  font-size: ${theme.typography.sizes.md};
  line-height: 1.7;
  color: ${theme.colors.neutral.darkGrey};
  margin-bottom: ${theme.spacing.md};
`;

const List = styled.ul`
  margin: ${theme.spacing.md} 0 ${theme.spacing.lg} ${theme.spacing.xl};
  
  li {
    font-size: ${theme.typography.sizes.md};
    line-height: 1.7;
    color: ${theme.colors.neutral.darkGrey};
    margin-bottom: ${theme.spacing.sm};
  }
`;

const Highlight = styled.div`
  background-color: ${theme.colors.neutral.lightGrey}20;
  border-left: 4px solid #FFD700;
  padding: ${theme.spacing.lg};
  margin: ${theme.spacing.xl} 0;
  
  p {
    margin: 0;
    font-size: ${theme.typography.sizes.sm};
    color: ${theme.colors.primary.dark};
    line-height: 1.6;
  }
`;

const Disclosures: React.FC = () => {
  return (
    <SharedLayoutUltimate>
      <Container>
        <Title>Important Disclosures & Legal Notices</Title>

        <Section>
          <SectionTitle>Educational Tool Only</SectionTitle>
          <Paragraph>
            The AnnuityAnswers.com performance testing tool is provided as an educational resource. 
            It is not a guarantee or prediction of future performance. While our testing uses daily, 
            actual historical market data for accuracy, all annuity performance projections are 
            inherently uncertain. The tool's outputs should be considered alongside other factors, 
            including your personal financial situation, objectives, and risk tolerance.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>No Fabricated Data</SectionTitle>
          <Paragraph>
            Our system intentionally avoids using fabricated or hypothetical backfilled index data 
            in its main output. Such data — created to simulate index history prior to actual existence — 
            is widely used for marketing purposes, but in our experience, it does not provide reliable 
            guidance for setting expectations. Where hypothetical data is shown for educational comparison, 
            it is clearly labeled.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Not Investment Advice</SectionTitle>
          <Paragraph>
            This tool was originally developed to select products for the Averill family portfolio 
            and is now offered to the public without charge as a good-faith resource. Use of this 
            tool does not constitute financial advice or a recommendation to buy or sell any product.
          </Paragraph>
          <Paragraph>
            <strong>Important:</strong> You should consult with qualified financial and legal advisors 
            before making any investment decisions. We are not registered investment advisors, and our 
            content should not be considered as personalized investment advice.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Acknowledgments and Limitations</SectionTitle>
          <Paragraph>
            <strong>By using this tool and website, you acknowledge that:</strong>
          </Paragraph>
          <List>
            <li>We are not predicting the future</li>
            <li>Past performance is not a guarantee of future results</li>
            <li>You are solely responsible for any financial decisions you make</li>
            <li>No investment should be made solely on the basis of this test</li>
            <li>Actual future results will differ from any projections</li>
            <li>Neither Averill Agencies, AnnuityAnswers.com, nor any associated persons are liable 
                for financial losses resulting from your reliance on the tool's outputs</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>Regulatory Compliance</SectionTitle>
          <Paragraph>
            Our methodology distinguishes between actual historical data and hypothetical performance 
            in accordance with NAIC Model Regulation 245. We provide both historical rates and 
            today's rates options to ensure relevance to current purchase decisions. All fabricated 
            or pre-inception data is clearly labeled when shown for comparison purposes.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Data Accuracy and Sources</SectionTitle>
          <Paragraph>
            While we strive to maintain accurate and up-to-date information, we cannot guarantee 
            the complete accuracy of all data presented. Market data is sourced from reliable 
            third-party providers, but delays or inaccuracies may occur. Users should verify 
            critical information independently before making financial decisions.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Privacy and Data Protection</SectionTitle>
          <Paragraph>
            We respect your privacy and are committed to protecting your personal information. 
            Any data you provide through our forms is used solely for the purposes stated and 
            is not sold or shared with third parties without your explicit consent, except as 
            required by law. For full details, please review our Privacy Policy.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Licensing and Insurance Information</SectionTitle>
          <Paragraph>
            Averill Agencies and its representatives are licensed insurance professionals. 
            Licensing information is available upon request. We maintain professional liability 
            insurance and adhere to all applicable state and federal regulations governing 
            insurance sales and marketing.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Proprietary Terms and Intellectual Property</SectionTitle>
          <Paragraph>
            Concepts such as "Rate Drift" and "Date Lottery" are proprietary to Averill Agencies. 
            Unauthorized reproduction of the tool, methodology, or proprietary terms is prohibited. 
            All content, including text, graphics, logos, and software, is protected by copyright 
            and other intellectual property laws.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Liability Limitations</SectionTitle>
          <Paragraph>
            To the fullest extent permitted by law, AnnuityAnswers.com and its affiliates shall 
            not be liable for any indirect, incidental, special, consequential, or punitive damages, 
            or any loss of profits or revenues, whether incurred directly or indirectly, or any 
            loss of data, use, goodwill, or other intangible losses.
          </Paragraph>
        </Section>

        <Highlight>
          <p>
            <strong>Contact Information:</strong> If you have questions about these disclosures 
            or need additional information, please contact us at 800-922-1894 or through our 
            contact form. We are available Monday through Friday, 9am-5pm EST.
          </p>
        </Highlight>

        <Highlight>
          <p>
            By submitting any form on this website or using our tools, you confirm that you have 
            read, understood, and agree to these disclosures and our terms of use. This page was 
            last updated on October 8, 2025.
          </p>
        </Highlight>
      </Container>
    </SharedLayoutUltimate>
  );
};

export default Disclosures;